import type { Channel } from '@/lib/channel';

/** Ein kanal-schaltbares Feature (Eintrag der Registry). */
export type ChannelFeature = {
  /** Stabiler Key — landet als JSON-Key in restaurant_settings.channel_flags. */
  key: string;
  /** Anzeigename in der Admin-UI (Einstellungen → System → Kanal-Features). */
  label: string;
  /** Optionale Kurzbeschreibung für die Admin-UI. */
  description?: string;
};

/**
 * Registry der kanal-schaltbaren Features (SSOT). Neues Flag in 3 Schritten
 * (CLAUDE.md „Kanal-Architektur"):
 *  1) Key + Label hier eintragen,
 *  2) im Consumer `useFeature('<key>')` abfragen,
 *  3) fertig — die Toggles erscheinen automatisch unter
 *     Einstellungen → System → Kanal-Features. Toggle wirkt ohne Deploy
 *     und ohne App-Store-Review (die Shell lädt die Prod-Domain).
 *
 * Semantik: fehlender Key/Teilkey ⇒ Feature AN (Default-on). Flags sind
 * über die anon-SELECT-Policy öffentlich lesbar — nichts Sensibles in
 * Keys oder Labels ablegen.
 *
 * 🚨 NICHT VERWECHSELN MIT `src/features/bestellsystem/freigaben.ts`.
 * Seit Migration 0114 steht daneben eine zweite jsonb-Spalte,
 * `restaurant_settings.bestellsystem_freigaben`, mit der GENAU UMGEKEHRTEN
 * Bedeutung: dort heißt ein fehlender Schlüssel AUS, und nur exakt `true`
 * öffnet. Beide Listen tragen einen Schlüssel namens ähnlich dem anderen —
 * hier `bestellsystem` (Sichtbarkeit, Default AN, nur Browser), dort `gesamt`
 * (Annahme am Server, Default AUS, sofort). `resolveFeature` unten ist
 * fail-OPEN und darf für den Not-Aus NIEMALS kopiert werden.
 *
 * Startet bewusst leer; Beispiel-Eintrag:
 *   { key: 'reservierung_kalender', label: 'Reservierungs-Kalender' },
 */
const REGISTRY = [
  {
    key: 'ape_verkauf',
    label: 'Ape-Verkauf',
    description:
      'Pizza-Übersicht für den Verkauf vor Ort. AUS blendet den Menüpunkt und die Verkaufsflächen aus — die erfassten Bestellungen bleiben unangetastet. Das ist die Rückfallebene für den Termin: Der Schalter wirkt in Minuten, ohne Deploy und ohne App-Review.',
  },
  {
    key: 'ape_tafel',
    label: 'Abholtafel',
    description:
      'Öffentliche Anzeige unter /tafel, die Gäste per QR-Code aufrufen: welche Bestellnummern noch in Arbeit sind und welche bereit zur Abholung. AUS zeigt stattdessen einen Hinweis, dass die Tafel gerade nicht verfügbar ist — der Verkauf selbst läuft weiter. Der Schalter wirkt in Minuten, ohne Deploy.',
  },
  {
    key: 'bestellsystem',
    label: 'Online-Bestellung',
    description:
      'Blendet die Online-Bestellung je Kanal aus oder ein — die Einstiege auf der Website und in der App. 🚨 Das ist NICHT der Not-Aus: dieser Schalter regelt nur die Sichtbarkeit, wirkt allein im Browser und braucht Minuten. Ob der Server eine Bestellung überhaupt annimmt, entscheiden die Bestellsystem-Freigaben im Abschnitt darunter — die wirken sofort und auf jedem Gerät. Grundstellung hier: AN. Dort: AUS.',
  },
] as const satisfies ReadonlyArray<ChannelFeature>;

/** Union der registrierten Keys — leer ⇒ never ⇒ useFeature unaufrufbar (gewollt). */
export type FeatureKey = (typeof REGISTRY)[number]['key'];

/** Widened Export für die Admin-UI (iteriert generisch über alle Einträge). */
export const FEATURE_REGISTRY: ReadonlyArray<ChannelFeature> = REGISTRY;

/** Format der DB-Spalte restaurant_settings.channel_flags (Migration 0071). */
export type ChannelFlags = Record<string, Partial<Record<Channel, boolean>>>;

/**
 * Pure Auswertung eines Flags: NUR explizites `false` schaltet ab.
 * Fehlender Key, fehlender Teilkey oder kaputte Daten ⇒ AN (Default-on) —
 * ein Flag kann ein Feature nur drosseln, nie versehentlich freischalten,
 * und die UI bleibt korrekt, solange Settings noch laden.
 */
export function resolveFeature(flags: unknown, channel: Channel, key: string): boolean {
  if (!flags || typeof flags !== 'object') return true;
  const entry = (flags as Record<string, unknown>)[key];
  if (!entry || typeof entry !== 'object') return true;
  return (entry as Record<string, unknown>)[channel] !== false;
}
